import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'

const COOKIE_CONSENT_KEY = 'fgautomazioni_cookie_consent'

export function CookieBanner() {
  const [isVisible, setIsVisible] = useState(false)
  const { t } = useTranslation()
  const { lang } = useParams<{ lang: string }>()

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY)
    if (!consent) {
      const timer = setTimeout(() => setIsVisible(true), 1000)
      return () => clearTimeout(timer)
    }
  }, [])

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted')
    setIsVisible(false)
  }

  const handleDecline = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined')
    setIsVisible(false)
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-3 left-3 right-3 z-50 max-w-sm rounded-xl border border-[var(--color-graphite)] bg-[var(--color-charcoal)] shadow-2xl sm:bottom-4 sm:left-4 sm:right-auto"
        >
          <div className="relative p-4 sm:p-5">
            {/* Close button */}
            <button
              onClick={handleDecline}
              className="absolute right-3 top-3 p-1 text-[var(--color-stone)] transition-colors hover:text-white"
              aria-label={t('aria.close')}
            >
              <X className="h-4 w-4" />
            </button>

            {/* Message */}
            <p className="mb-4 pr-5 text-sm leading-relaxed text-[var(--color-fog)]">
              {t('cookie.message')}{' '}
              <Link
                to={`/${lang ?? 'it'}/cookie-policy`}
                className="text-[var(--accent-primary)] hover:underline"
              >
                {t('cookie.learnMore')}
              </Link>
            </p>

            {/* Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleDecline}
                className="flex-1 rounded-lg border border-[var(--color-graphite)] bg-transparent px-4 py-2 text-sm font-medium text-[var(--color-fog)] transition-colors hover:bg-[var(--color-graphite)]"
              >
                {t('buttons.decline')}
              </button>
              <button
                onClick={handleAccept}
                className="flex-1 rounded-lg bg-[var(--accent-primary)] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--color-copper-400)]"
              >
                {t('buttons.accept')}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
